/**
 * Purchase places + household presence — desktop sidebar destination.
 *
 * View-only: pins come from the snapshot and GET /presence. The browser never
 * becomes a GPS client; capture stays on the iOS app.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import type { Map as MapLibreMap, Marker, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  formatMoney,
  longDate,
  merchantName,
  type Presence,
  type Transaction,
} from '@alohamula/domain';
import { PageHeader } from '../components/AppShell';
import { Button, EmptyState, SegmentedControl } from '../components/ui';
import { MapIcon } from '../components/icons';
import { usePresence } from '../api/queries';
import type { Budget } from '../budget/useBudget';
import './pages.css';

type MapRange = 'month' | 'all';

const RANGE_OPTIONS = [
  { value: 'month' as const, label: 'This month' },
  { value: 'all' as const, label: 'All' },
];

/** OSM raster style — attribution stays on the map control. */
const OSM_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

interface MapPageProps {
  budget: Budget;
  now: Date;
}

export function MapPage({ budget, now }: MapPageProps) {
  const [range, setRange] = useState<MapRange>('month');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const presenceQuery = usePresence();

  const purchases = useMemo(
    () => locatedPurchases(budget.transactions, range, now),
    [budget.transactions, range, now]
  );

  const members = useMemo(
    () => (presenceQuery.data?.presence ?? []).filter((row) => row.sharingEnabled),
    [presenceQuery.data?.presence]
  );

  const home = presenceQuery.data?.home ?? null;
  const selected = purchases.find((tx) => tx.id === selectedId) ?? null;

  const mapHostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<Marker[]>([]);
  const fittedKeyRef = useRef<string>('');

  // Create the map once.
  useEffect(() => {
    const host = mapHostRef.current;
    if (!host || mapRef.current) return;

    const map = new maplibregl.Map({
      container: host,
      style: OSM_STYLE,
      center: [-157.8, 21.3],
      zoom: 10,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    return () => {
      for (const marker of markersRef.current) marker.remove();
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Sync markers whenever pins change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => {
      for (const marker of markersRef.current) marker.remove();
      markersRef.current = [];

      const bounds = new maplibregl.LngLatBounds();
      let hasPoint = false;

      for (const tx of purchases) {
        const lng = tx.longitude!;
        const lat = tx.latitude!;
        bounds.extend([lng, lat]);
        hasPoint = true;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = `map-pin map-pin--purchase${selectedId === tx.id ? ' is-selected' : ''}`;
        el.setAttribute('aria-label', merchantName(tx.description));
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          setSelectedId(tx.id);
        });

        markersRef.current.push(new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map));
      }

      for (const member of members) {
        bounds.extend([member.longitude, member.latitude]);
        hasPoint = true;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'map-pin map-pin--presence';
        el.textContent = member.displayName.trim().charAt(0).toUpperCase() || '?';
        el.setAttribute(
          'aria-label',
          `${member.displayName}${isStale(member) ? ', updated earlier' : ', live'}`
        );
        el.title = member.displayName;

        markersRef.current.push(
          new maplibregl.Marker({ element: el })
            .setLngLat([member.longitude, member.latitude])
            .addTo(map)
        );
      }

      if (home) {
        bounds.extend([home.longitude, home.latitude]);
        hasPoint = true;

        const el = document.createElement('div');
        el.className = 'map-pin map-pin--home';
        el.setAttribute('aria-label', 'Home');
        el.title = 'Home';

        markersRef.current.push(
          new maplibregl.Marker({ element: el }).setLngLat([home.longitude, home.latitude]).addTo(map)
        );
      }

      const fitKey = [
        range,
        purchases.map((tx) => tx.id).join(','),
        members.map((m) => m.cognitoSub).join(','),
        home ? `${home.latitude},${home.longitude}` : '',
      ].join('|');

      if (hasPoint && fittedKeyRef.current !== fitKey) {
        fittedKeyRef.current = fitKey;
        map.fitBounds(bounds, { padding: 56, maxZoom: 14, duration: 0 });
      }
    };

    if (map.loaded()) apply();
    else map.once('load', apply);
  }, [purchases, members, home, selectedId, range]);

  const showEmpty =
    purchases.length === 0 && members.length === 0 && !home && !presenceQuery.isPending;

  return (
    <>
      <PageHeader
        title="Map"
        subtitle="Purchases tagged from a shared place"
        action={
          <SegmentedControl
            label="Date range"
            value={range}
            options={RANGE_OPTIONS}
            onChange={setRange}
          />
        }
      />

      <div className="map-page">
        <div className="map-page__stage" role="region" aria-label="Purchase map">
          <div ref={mapHostRef} className="map-page__canvas" />

          {showEmpty && (
            <div className="map-page__empty">
              <EmptyState
                title="No places yet"
                body={
                  range === 'month'
                    ? 'No purchases this month have a place. Try All, or wait for a charge tagged from someone’s last location.'
                    : 'When a new charge arrives, AlohaMula tags it from a household member’s last place — if someone was out. Capture runs on the iPhone, not in this browser.'
                }
              />
            </div>
          )}

          {presenceQuery.isError && (
            <p className="map-page__banner" role="status">
              Could not load who’s sharing location.
              <Button
                emphasis="ghost"
                size="sm"
                onClick={() => void presenceQuery.refetch()}
              >
                Retry
              </Button>
            </p>
          )}
        </div>

        {selected && (
          <PurchaseDetail
            transaction={selected}
            onClose={() => setSelectedId(null)}
            now={now}
          />
        )}

        {!selected && (members.length > 0 || home) && (
          <aside className="map-page__legend" aria-label="Map legend">
            <span className="map-page__legend-item">
              <MapIcon width={14} height={14} />
              {purchases.length} purchase{purchases.length === 1 ? '' : 's'}
            </span>
            {members.length > 0 && (
              <span className="map-page__legend-item">
                {members.map((m) => m.displayName).join(', ')} sharing
              </span>
            )}
            {home && <span className="map-page__legend-item">Home pinned</span>}
          </aside>
        )}
      </div>
    </>
  );
}

function PurchaseDetail({
  transaction,
  onClose,
  now,
}: {
  transaction: Transaction;
  onClose: () => void;
  now: Date;
}) {
  const captured = transaction.locationCapturedAt
    ? formatCapturedAt(transaction.locationCapturedAt)
    : null;

  return (
    <aside className="map-detail" aria-label="Purchase details">
      <div className="map-detail__head">
        <div>
          <h2 className="map-detail__title">{merchantName(transaction.description)}</h2>
          <p className="map-detail__meta">
            <span className="numeric">{formatMoney(Math.abs(transaction.amount))}</span>
            {' · '}
            {longDate(transaction.transactionDate, now)}
          </p>
        </div>
        <Button emphasis="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      <dl className="map-detail__facts">
        {captured && (
          <>
            <dt>Tagged</dt>
            <dd>{captured}</dd>
          </>
        )}
        {transaction.locationDisplayName?.trim() && (
          <>
            <dt>Account</dt>
            <dd>{transaction.locationDisplayName.trim()}</dd>
          </>
        )}
        {transaction.locationSource && (
          <>
            <dt>How</dt>
            <dd>{sourceLabel(transaction.locationSource)}</dd>
          </>
        )}
      </dl>
    </aside>
  );
}

function locatedPurchases(
  transactions: Transaction[],
  range: MapRange,
  now: Date
): Transaction[] {
  const prefix = monthPrefix(now);
  return transactions.filter((tx) => {
    if (tx.deletedAt != null) return false;
    if (!(tx.amount < 0)) return false;
    if (tx.latitude == null || tx.longitude == null) return false;
    if (!Number.isFinite(tx.latitude) || !Number.isFinite(tx.longitude)) return false;
    if (range === 'month' && !tx.transactionDate.startsWith(prefix)) return false;
    return true;
  });
}

/** Ledger months are Eastern calendar months, same as the iOS map. */
function monthPrefix(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const month = parts.find((p) => p.type === 'month')?.value ?? '01';
  return `${year}-${month}`;
}

function formatCapturedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function sourceLabel(source: NonNullable<Transaction['locationSource']>): string {
  switch (source) {
    case 'presence':
      return 'From live location';
    case 'arrival':
      return 'When the charge arrived';
    case 'manual':
      return 'Placed by hand';
    default:
      return source;
  }
}

function isStale(member: Presence): boolean {
  const updated = Date.parse(member.updatedAt);
  if (!Number.isFinite(updated)) return true;
  return Date.now() - updated > 15 * 60_000;
}
